/**
 * Age Gate Component
 * Blocks quiz access until user confirms they're of legal age
 */

import { createLogger } from '../core/logger';
import { getState, setAgeVerified } from '../core/state';

const logger = createLogger('AgeGate');

const AGE_GATE_STORAGE_KEY = 'ageVerified';

/**
 * Check if age gate should be shown
 */
export function shouldShowAgeGate(showAgeGate: boolean): boolean {
  if (!showAgeGate) return false;

  // Check if already verified in state
  if (getState().compliance.ageVerified) return false;

  // Check sessionStorage
  try {
    if (sessionStorage.getItem(AGE_GATE_STORAGE_KEY) === 'true') {
      setAgeVerified();
      return false;
    }
  } catch {
    // Storage unavailable
  }

  return true;
}

/**
 * Show the age gate modal
 * Returns a promise that resolves when user is verified, rejects if denied
 */
export function showAgeGate(): Promise<void> {
  return new Promise((resolve, reject) => {
    logger.info('Showing age gate');

    // Create overlay
    const overlay = document.createElement('div');
    overlay.id = 'age-gate-overlay';
    overlay.className = 'modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'age-gate-title');

    overlay.innerHTML = `
      <div class="modal age-gate-modal">
        <div class="modal-header">
          <div class="modal-icon">🔞</div>
          <h2 class="modal-title" id="age-gate-title">Age Verification Required</h2>
          <p class="modal-subtitle">You must be 18 years or older to continue</p>
        </div>

        <div class="modal-body">
          <p>This content is intended for adults only. By clicking "I'm 18+" you confirm that you are at least 18 years of age.</p>
        </div>

        <div class="modal-footer age-gate-buttons">
          <button type="button" class="btn btn-secondary" id="age-gate-no">
            I'm Under 18
          </button>
          <button type="button" class="btn btn-primary" id="age-gate-yes">
            I'm 18+
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Animate in
    requestAnimationFrame(() => {
      overlay.classList.add('is-visible');
    });

    // Handle Yes
    document.getElementById('age-gate-yes')?.addEventListener('click', () => {
      logger.info('Age verified');
      setAgeVerified();

      // Store in sessionStorage
      try {
        sessionStorage.setItem(AGE_GATE_STORAGE_KEY, 'true');
      } catch {
        // Storage unavailable
      }

      // Close modal
      overlay.classList.remove('is-visible');
      setTimeout(() => overlay.remove(), 200);

      resolve();
    });

    // Handle No
    document.getElementById('age-gate-no')?.addEventListener('click', () => {
      logger.info('Age verification denied');

      // Show blocked state
      const modal = overlay.querySelector('.modal');
      if (modal) {
        modal.innerHTML = `
          <div class="modal-header">
            <div class="modal-icon">🚫</div>
            <h2 class="modal-title">Access Denied</h2>
          </div>
          <div class="modal-body">
            <p>Sorry, you must be 18 years or older to access this content.</p>
            <p>Please close this page.</p>
          </div>
        `;
      }

      reject(new Error('Age verification denied'));
    });

    // Focus the Yes button
    setTimeout(() => {
      document.getElementById('age-gate-yes')?.focus();
    }, 100);
  });
}
